import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useSEO, getWebPageSchema, getBreadcrumbSchema, getOrganizationSchema } from '../../utils/seo';

export default function AProposPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'À propos - Notre agence web',
        'Agence digitale experte en sites business, design conversion, rédaction et SEO. 8 ans d\'expérience, 50+ sites créés.',
        `${siteUrl}/a-propos`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'À propos', url: '/a-propos' }
      ]),
      getOrganizationSchema(),
      {
        '@type': 'AboutPage',
        name: 'À propos de THE 21-DAY BUSINESS WEBSITE™',
        description: 'Notre mission, nos valeurs et notre équipe'
      }
    ]
  };

  useSEO({
    title: 'À propos - Notre agence web | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Agence digitale experte en sites business, design conversion, rédaction et SEO. 8 ans d\'expérience, 50+ sites créés, 100% de clients satisfaits.',
    keywords: 'agence web, création site web, équipe web, agence digitale, expertise web',
    canonical: `${siteUrl}/a-propos`,
    schema
  });

  const values = [
    {
      icon: 'ri-star-line',
      title: 'Excellence',
      description: 'Nous ne faisons aucun compromis sur la qualité de nos livrables'
    },
    {
      icon: 'ri-time-line',
      title: 'Rapidité',
      description: 'Nous respectons nos engagements et livrons dans les délais'
    },
    {
      icon: 'ri-heart-line',
      title: 'Passion',
      description: 'Nous aimons ce que nous faisons et ça se voit dans nos créations'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Écoute',
      description: 'Votre satisfaction est au cœur de notre processus'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              À propos de <span className="text-yellow-600">nous</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Une agence digitale experte en sites business, design conversion, rédaction et SEO
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                  Notre mission
                </h2>
                <p className="text-xl text-slate-600 mb-6 leading-relaxed">
                  Nous aidons les entrepreneurs et les professionnels à créer une présence en ligne qui reflète leur expertise et génère des résultats concrets.
                </p>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Trop de sites business sont créés sans stratégie, avec des templates génériques qui ne convertissent pas. Nous avons créé THE 21-DAY BUSINESS WEBSITE™ pour offrir une alternative : un site sur mesure, pensé pour la conversion, livré rapidement.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Notre approche combine design premium, stratégie digitale, rédaction professionnelle et optimisation SEO pour créer des sites qui travaillent vraiment pour votre business.
                </p>
              </div>
              
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/d617bccc2fcaa61058c1c4f49145e565.jpg"
                    alt="Notre équipe"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Nos valeurs
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Ce qui guide notre travail au quotidien
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-2xl mx-auto mb-6">
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-500 mb-2">8+</div>
                <div className="text-slate-300">Années d'expérience</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-500 mb-2">50+</div>
                <div className="text-slate-300">Sites créés</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-amber-400 mb-2">100%</div>
                <div className="text-slate-300">Clients satisfaits</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-amber-400 mb-2">3</div>
                <div className="text-slate-300">Semaines de livraison</div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

